import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useLocation } from "wouter";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { trpc } from "@/lib/trpc";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  ArrowDownUp,
  BarChart3,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  Download,
  Filter,
  MapPin,
  Package,
  Plus,
  Search,
  Upload,
  UserCheck,
  Users,
  X,
  History,
} from "lucide-react";
import { AuditLogDialog } from "@/components/AuditLogDialog";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { EditableCell } from "@/components/EditableCell";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Camera } from "lucide-react";

type MovementType = "ENTRADA" | "INTERNA" | "SAIDA" | "REPOSICAO";
type MovementStatus = "ABERTO" | "EM_ATENDIMENTO" | "CONCLUIDO" | "CANCELADO";

export default function Queue() {
  const { user, loading: authLoading } = useGoogleAuth();
  const [search, setSearch] = useState("");
  const [tipoFilter, setTipoFilter] = useState<MovementType | "ALL">("ALL");
  const [statusFilter, setStatusFilter] = useState<MovementStatus | "ALL">("ALL");
  const [periodoFilter, setPeriodoFilter] = useState<"ALL" | "TODAY" | "LAST_7" | "LAST_30">("ALL");
  const [showNewForm, setShowNewForm] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [sortField, setSortField] = useState<"id" | "tipo" | "status" | "produto" | "referencia" | "numeroPedido" | "criadoEm" | "origem" | "destino" | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [, setLocation] = useLocation();
  
  // Estados para atribuição em massa
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [showAssignBar, setShowAssignBar] = useState(false);
  
  // Estado para filtro "Minhas Tarefas"
  const [showOnlyMyTasks, setShowOnlyMyTasks] = useState(false);
  
  // Estados para dialog de confirmação com fotos
  const [showPhotoDialog, setShowPhotoDialog] = useState(false);
  const [selectedMovementId, setSelectedMovementId] = useState<number | null>(null);
  const [fotoProduto, setFotoProduto] = useState<string | null>(null);
  const [fotoEndereco, setFotoEndereco] = useState<string | null>(null);
  
  // Estados para auditoria
  const [auditDialogOpen, setAuditDialogOpen] = useState(false);
  const [selectedMovementForAudit, setSelectedMovementForAudit] = useState<number | null>(null);
  
  // Estados para paginação
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 100;

  // Redirecionar para login se não autenticado
  useEffect(() => {
    if (!authLoading && !user) {
      window.location.href = "/login";
    }
  }, [user, authLoading]);
  
  // Ativar filtro "Minhas Tarefas" automaticamente para operadores
  useEffect(() => {
    if (user && (user.role === "operador" || user.role === "user")) {
      setShowOnlyMyTasks(true);
    }
  }, [user]);
  
  // Mostrar barra de atribuição quando itens são selecionados
  useEffect(() => {
    setShowAssignBar(selectedIds.length > 0);
  }, [selectedIds]);

  // Calcular datas de início e fim baseado no filtro de período
  const getDateRange = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (periodoFilter) {
      case "TODAY":
        return { startDate: today, endDate: undefined };
      case "LAST_7":
        const last7 = new Date(today);
        last7.setDate(last7.getDate() - 7);
        return { startDate: last7, endDate: undefined };
      case "LAST_30":
        const last30 = new Date(today);
        last30.setDate(last30.getDate() - 30);
        return { startDate: last30, endDate: undefined };
      default:
        return { startDate: undefined, endDate: undefined };
    }
  };

  const dateRange = getDateRange();

  // Query para listar movimentações
  const { data: rawMovements = [], refetch } = trpc.movements.list.useQuery(
    {
      search: search || undefined,
      tipo: tipoFilter !== "ALL" ? tipoFilter : undefined,
      status: statusFilter !== "ALL" ? statusFilter : undefined,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      limit: 100,
    },
    { enabled: !!user }
  );
  
  // Query para auditoria
  const { data: auditLogs = [], isLoading: auditLoading } = trpc.movements.auditLog.useQuery(
    { movementId: selectedMovementForAudit! },
    { enabled: !!selectedMovementForAudit && auditDialogOpen }
  );

  // Aplicar filtro de "Minhas Tarefas" se ativo
  let filteredMovements = rawMovements;
  if (showOnlyMyTasks && user) {
    filteredMovements = rawMovements.filter(m => m.responsavelId === user.id);
  }
  
  // Aplicar ordenação
  const sortedMovements = [...filteredMovements].sort((a, b) => {
    if (!sortField) return 0;

    let aValue: any;
    let bValue: any;

    switch (sortField) {
      case "id":
        aValue = a.id;
        bValue = b.id;
        break;
      case "tipo":
        aValue = a.tipo;
        bValue = b.tipo;
        break;
      case "status":
        aValue = a.status;
        bValue = b.status;
        break;
      case "produto":
        aValue = (a.produto || "").toLowerCase();
        bValue = (b.produto || "").toLowerCase();
        break;
      case "referencia":
        aValue = (a.referencia || "").toLowerCase();
        bValue = (b.referencia || "").toLowerCase();
        break;
      case "numeroPedido":
        aValue = (a.numeroPedido || "").toLowerCase();
        bValue = (b.numeroPedido || "").toLowerCase();
        break;
      case "criadoEm":
        aValue = new Date(a.criadoEm).getTime();
        bValue = new Date(b.criadoEm).getTime();
        break;
      case "origem":
        aValue = (a.origem || "").toLowerCase();
        bValue = (b.origem || "").toLowerCase();
        break;
      case "destino":
        aValue = (a.destino || "").toLowerCase();
        bValue = (b.destino || "").toLowerCase();
        break;

      default:
        return 0;
    }

    if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
    if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });
  
  // Aplicar paginação
  const totalItems = sortedMovements.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const movements = sortedMovements.slice(startIndex, endIndex);
  
  // Resetar para página 1 quando filtros mudarem
  useEffect(() => {
    setCurrentPage(1);
  }, [search, tipoFilter, statusFilter, periodoFilter, showOnlyMyTasks]);

  // Atalho Ctrl+K para focar no campo de busca
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        const searchInput = document.querySelector('input[placeholder="Buscar produto, origem, destino..."]') as HTMLInputElement;
        if (searchInput) {
          searchInput.focus();
          searchInput.select();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Query para estatísticas
  const { data: stats } = trpc.movements.stats.useQuery(undefined, {
    enabled: !!user,
  });

  const utils = trpc.useUtils();

  // Mutation para atualizar status
  const updateStatusMutation = trpc.movements.updateStatus.useMutation({
    onSuccess: () => {
      refetch();
      utils.movements.stats.invalidate();
      toast.success("Status atualizado com sucesso");
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  // Mutation para atualizar campos inline
  const updateFieldMutation = trpc.movements.update.useMutation({
    onSuccess: () => {
      refetch();
      toast.success("Campo atualizado");
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
  
  // Mutation para atribuição em massa
  const assignMutation = trpc.movements.assign.useMutation({
    onSuccess: () => {
      refetch();
      setSelectedIds([]);
      setShowAssignBar(false);
      toast.success("Movimentações atribuídas com sucesso");
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
  
  // Query para listar operadores (apenas líderes)
  const { data: operators = [] } = trpc.users.listOperators.useQuery(
    undefined,
    { enabled: !!user && (user.role === "lider" || user.role === "admin") }
  );

  const handleSort = (field: "id" | "tipo" | "status" | "produto" | "referencia" | "numeroPedido" | "criadoEm" | "origem" | "destino") => {
    if (sortField === field) {
      // Alternar direção
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // Novo campo, começar com ascendente
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const handlePegar = (id: number) => {
    updateStatusMutation.mutate({ id, status: "EM_ATENDIMENTO" });
  };

  const handleConcluir = (id: number) => {
    // Abrir dialog para capturar fotos antes de concluir
    setSelectedMovementId(id);
    setFotoProduto(null);
    setFotoEndereco(null);
    setShowPhotoDialog(true);
  };
  
  const handleConfirmWithPhotos = async () => {
    if (!fotoProduto || !fotoEndereco) {
      toast.error("Por favor, capture ambas as fotos antes de concluir");
      return;
    }
    
    if (!selectedMovementId) return;
    
    // Enviar fotos em base64 para o backend fazer upload no S3
    updateStatusMutation.mutate({ 
      id: selectedMovementId, 
      status: "CONCLUIDO",
      fotoProdutoBase64: fotoProduto,
      fotoEnderecoBase64: fotoEndereco,
    });
    
    setShowPhotoDialog(false);
    setSelectedMovementId(null);
    setFotoProduto(null);
    setFotoEndereco(null);
  };

  const handleUpdateField = (id: number, field: string, value: string) => {
    updateFieldMutation.mutate({
      id,
      [field]: value || undefined,
    });
  };

  const handleCancelar = (id: number) => {
    const motivo = prompt("Motivo do cancelamento:");
    if (motivo) {
      updateStatusMutation.mutate({ id, status: "CANCELADO", motivoCancelamento: motivo });
    }
  };
  
  // Handlers para atribuição em massa
  const handleToggleSelect = (id: number) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };
  
  const handleSelectAll = () => {
    if (selectedIds.length === movements.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(movements.map(m => m.id));
    }
  };
  
  const handleAssign = (operatorId: number) => {
    if (selectedIds.length === 0) {
      toast.error("Selecione pelo menos uma movimentação");
      return;
    }
    assignMutation.mutate({
      movementIds: selectedIds,
      responsavelId: operatorId,
    });
  };
  
  const handleToggleMyTasks = () => {
    setShowOnlyMyTasks(prev => !prev);
  };
  
  const handleCapturePhoto = (type: 'produto' | 'endereco') => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment'; // Usar câmera traseira
    
    input.onchange = (e: Event) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64 = reader.result as string;
          if (type === 'produto') {
            setFotoProduto(base64);
          } else {
            setFotoEndereco(base64);
          }
        };
        reader.readAsDataURL(file);
      }
    };
    
    input.click();
  };

  const handleExportExcel = async () => {
    try {
      const XLSX = await import("xlsx");
      
      const exportData = sortedMovements.map((m) => ({
        ID: m.id,
        Status: m.status,
        Tipo: m.tipo,
        Produto: m.produto,
        Referência: m.referencia || "-",
        "Nº Pedido": m.numeroPedido || "-",
        Quantidade: m.quantidade ? parseInt(m.quantidade) : "-",
        Criado: new Date(m.criadoEm).toLocaleString("pt-BR"),
        Origem: m.origem,
        Destino: m.destino,
        "Removido Totalmente": (m.tipo === "INTERNA" || m.tipo === "REPOSICAO") ? (m.removidoTotalmente ? "Sim" : "Não") : "-",
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Movimentações");
      XLSX.writeFile(wb, `movimentacoes_${new Date().toISOString().split("T")[0]}.xlsx`);
      
      toast.success("Exportação Excel concluída");
      setShowExportMenu(false);
    } catch (error) {
      toast.error("Erro ao exportar Excel");
    }
  };

  const handleExportPDF = async () => {
    try {
      const { jsPDF } = await import("jspdf");
      const autoTable = (await import("jspdf-autotable")).default;
      
      const doc = new jsPDF();
      
      doc.setFontSize(16);
      doc.text("Fila de Movimentações", 14, 15);
      doc.setFontSize(10);
      doc.text(`Gerado em: ${new Date().toLocaleString("pt-BR")}`, 14, 22);
      
      const tableData = sortedMovements.map((m) => [
        m.id,
        m.status,
        m.tipo,
        m.produto,
        m.referencia || "-",
        m.numeroPedido || "-",
        m.quantidade ? parseInt(m.quantidade).toLocaleString("pt-BR") : "-",
        new Date(m.criadoEm).toLocaleString("pt-BR"),
        m.origem,
        m.destino,
      ]);
      
      autoTable(doc, {
        head: [["ID", "Status", "Tipo", "Produto", "Referência", "Nº Pedido", "Qtd", "Criado", "Origem", "Destino"]],
        body: tableData,
        startY: 28,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [249, 115, 22] },
      });
      
      doc.save(`movimentacoes_${new Date().toISOString().split("T")[0]}.pdf`);
      
      toast.success("Exportação PDF concluída");
      setShowExportMenu(false);
    } catch (error) {
      toast.error("Erro ao exportar PDF");
    }
  };

  const getStatusColor = (status: MovementStatus) => {
    switch (status) {
      case "ABERTO":
        return "text-orange-600 bg-orange-50";
      case "EM_ATENDIMENTO":
        return "text-blue-600 bg-blue-50";
      case "CONCLUIDO":
        return "text-green-600 bg-green-50";
      case "CANCELADO":
        return "text-gray-600 bg-gray-50";
    }
  };

  const getTipoColor = (tipo: MovementType) => {
    switch (tipo) {
      case "ENTRADA":
        return "text-purple-600 bg-purple-50";
      case "INTERNA":
        return "text-blue-600 bg-blue-50";
      case "SAIDA":
        return "text-green-600 bg-green-50";
      case "REPOSICAO":
        return "text-amber-600 bg-amber-50";
    }
  };

  const formatStatus = (status: MovementStatus) => {
    return status.replace("_", " ");
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header fixo */}
      <div className="sticky top-0 z-10 bg-card border-b shadow-sm">
        <div className="container py-4">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-foreground">
              Fila de Movimentações
            </h1>
            <div className="flex gap-2">
              {/* Botão Dashboard (apenas líderes) */}
              {user && (user.role === "lider" || user.role === "admin") && (
                <Button
                  onClick={() => setLocation("/dashboard")}
                  size="sm"
                  variant="outline"
                  className="gap-2"
                >
                  <BarChart3 className="h-4 w-4" />
                  Dashboard
                </Button>
              )}
              <Button
                onClick={() => setLocation("/history")}
                size="sm"
                variant="outline"
                className="gap-2"
              >
                <Clock className="h-4 w-4" />
                Histórico
              </Button>
              <Button
                onClick={() => setLocation("/import")}
                size="sm"
                variant="outline"
                className="gap-2"
              >
                <Upload className="h-4 w-4" />
                Importar
              </Button>
              <Button
                onClick={() => setShowExportMenu(!showExportMenu)}
                size="sm"
                variant="outline"
                className="gap-2"
              >
                <Download className="h-4 w-4" />
                Exportar
              </Button>
              <Button
                onClick={() => setShowNewForm(!showNewForm)}
                size="sm"
                className="gap-2"
              >
                {showNewForm ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                {showNewForm ? "Fechar" : "Nova"}
              </Button>
            </div>
          </div>

          {/* Menu de Exportação */}
          {showExportMenu && (
            <div className="mb-4 p-3 bg-muted rounded-lg border">
              <p className="text-sm font-medium mb-2">Exportar dados filtrados:</p>
              <div className="flex gap-2">
                <Button
                  onClick={handleExportExcel}
                  size="sm"
                  variant="outline"
                  className="gap-2"
                >
                  <Download className="h-4 w-4" />
                  Excel
                </Button>
                <Button
                  onClick={handleExportPDF}
                  size="sm"
                  variant="outline"
                  className="gap-2"
                >
                  <Download className="h-4 w-4" />
                  PDF
                </Button>
              </div>
            </div>
          )}

          {/* Contadores */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="bg-orange-50 rounded-lg p-3 border border-orange-200">
              <div className="flex items-center gap-2 mb-1">
                <Clock className="h-4 w-4 text-orange-600" />
                <span className="text-xs font-medium text-orange-900">Abertos</span>
              </div>
              <p className="text-2xl font-bold text-orange-600">
                {stats?.abertos || 0}
              </p>
            </div>
            <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
              <div className="flex items-center gap-2 mb-1">
                <Package className="h-4 w-4 text-blue-600" />
                <span className="text-xs font-medium text-blue-900">Em Atendimento</span>
              </div>
              <p className="text-2xl font-bold text-blue-600">
                {stats?.emAtendimento || 0}
              </p>
            </div>
            <div className="bg-green-50 rounded-lg p-3 border border-green-200">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <span className="text-xs font-medium text-green-900">Hoje</span>
              </div>
              <p className="text-2xl font-bold text-green-600">
                {stats?.concluidosHoje || 0}
              </p>
            </div>
          </div>

          {/* Busca e filtros */}
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar produto, origem, destino..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select
              value={tipoFilter}
              onValueChange={(v) => setTipoFilter(v as MovementType | "ALL")}
            >
              <SelectTrigger className="w-full sm:w-[140px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Todos Tipos</SelectItem>
                <SelectItem value="ENTRADA">Entrada</SelectItem>
                <SelectItem value="INTERNA">Interna</SelectItem>
                <SelectItem value="SAIDA">Saída</SelectItem>
                <SelectItem value="REPOSICAO">Reposição</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={statusFilter}
              onValueChange={(v) => setStatusFilter(v as MovementStatus | "ALL")}
            >
              <SelectTrigger className="w-[180px]">
                <Filter className="mr-2 h-4 w-4" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Todos Status</SelectItem>
                <SelectItem value="ABERTO">Aberto</SelectItem>
                <SelectItem value="EM_ATENDIMENTO">Em Atendimento</SelectItem>
                <SelectItem value="CONCLUIDO">Concluído</SelectItem>
                <SelectItem value="CANCELADO">Cancelado</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={periodoFilter}
              onValueChange={(v) => setPeriodoFilter(v as "ALL" | "TODAY" | "LAST_7" | "LAST_30")}
            >
              <SelectTrigger className="w-full sm:w-[160px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Todos Períodos</SelectItem>
                <SelectItem value="TODAY">Hoje</SelectItem>
                <SelectItem value="LAST_7">Últimos 7 dias</SelectItem>
                <SelectItem value="LAST_30">Últimos 30 dias</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Botão Minhas Tarefas (visível apenas para líderes, sempre ativo para operadores) */}
            {user && (user.role === "lider" || user.role === "admin") && (
              <Button
                onClick={handleToggleMyTasks}
                size="sm"
                variant={showOnlyMyTasks ? "default" : "outline"}
                className="gap-2"
              >
                <UserCheck className="h-4 w-4" />
                {showOnlyMyTasks ? "Todas" : "Minhas"}
              </Button>
            )}
          </div>
          
          {/* Badge indicando filtro ativo */}
          {showOnlyMyTasks && (
            <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-blue-50 border border-blue-200 rounded-full text-xs font-medium text-blue-700">
              <UserCheck className="h-3 w-3" />
              Exibindo apenas minhas tarefas
            </div>
          )}
        </div>
      </div>

      {/* Tabela de movimentações */}
      <div className="container py-4">
        {showNewForm && (
          <div className="mb-4 p-4 bg-card rounded-lg border shadow-sm">
            <NewMovementForm onSuccess={() => {
              setShowNewForm(false);
              refetch();
            }} />
          </div>
        )}

        <div className="bg-card rounded-lg border shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
               <thead className="bg-muted/50">
                <tr>
                  {/* Checkbox para seleção em massa (apenas líderes) */}
                  {user && (user.role === "lider" || user.role === "admin") && (
                    <th className="text-center py-2 px-3 w-10">
                      <Checkbox
                        checked={selectedIds.length === movements.length && movements.length > 0}
                        onCheckedChange={handleSelectAll}
                      />
                    </th>
                  )}
                  <th
                    className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground cursor-pointer hover:text-foreground transition-colors select-none"
                    onClick={() => handleSort("id")}
                  >
                    <div className="flex items-center justify-center gap-1">
                      ID
                      {sortField === "id" && (
                        sortDirection === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
                      )}
                    </div>
                  </th>
                  <th
                    className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground cursor-pointer hover:text-foreground transition-colors select-none border-l"
                    onClick={() => handleSort("status")}
                  >
                    <div className="flex items-center justify-center gap-1">
                      Status
                      {sortField === "status" && (
                        sortDirection === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
                      )}
                    </div>
                  </th>
                  <th
                    className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground cursor-pointer hover:text-foreground transition-colors select-none border-l"
                    onClick={() => handleSort("tipo")}
                  >
                    <div className="flex items-center justify-center gap-1">
                      Tipo
                      {sortField === "tipo" && (
                        sortDirection === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
                      )}
                    </div>
                  </th>
                  <th
                    className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground cursor-pointer hover:text-foreground transition-colors select-none border-l"
                    onClick={() => handleSort("produto")}
                  >
                    <div className="flex items-center justify-center gap-1">
                      Produto
                      {sortField === "produto" && (
                        sortDirection === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
                      )}
                    </div>
                  </th>
                  <th
                    className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground cursor-pointer hover:text-foreground transition-colors select-none border-l"
                    onClick={() => handleSort("referencia")}
                  >
                    <div className="flex items-center justify-center gap-1">
                      Referência
                      {sortField === "referencia" && (
                        sortDirection === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
                      )}
                    </div>
                  </th>
                  <th
                    className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground cursor-pointer hover:text-foreground transition-colors select-none border-l"
                    onClick={() => handleSort("numeroPedido")}
                  >
                    <div className="flex items-center justify-center gap-1">
                      Nº Pedido
                      {sortField === "numeroPedido" && (
                        sortDirection === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
                      )}
                    </div>
                  </th>
                  <th className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground border-l">
                    Qtd
                  </th>
                  <th
                    className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground cursor-pointer hover:text-foreground transition-colors select-none border-l"
                    onClick={() => handleSort("criadoEm")}
                  >
                    <div className="flex items-center justify-center gap-1">
                      Criado
                      {sortField === "criadoEm" && (
                        sortDirection === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
                      )}
                    </div>
                  </th>
                  <th
                    className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground cursor-pointer hover:text-foreground transition-colors select-none border-l hidden sm:table-cell"
                    onClick={() => handleSort("origem")}
                  >
                    <div className="flex items-center justify-center gap-1">
                      Origem
                      {sortField === "origem" && (
                        sortDirection === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
                      )}
                    </div>
                  </th>
                  <th
                    className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground cursor-pointer hover:text-foreground transition-colors select-none border-l hidden sm:table-cell"
                    onClick={() => handleSort("destino")}
                  >
                    <div className="flex items-center justify-center gap-1">
                      Destino
                      {sortField === "destino" && (
                        sortDirection === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
                      )}
                    </div>
                  </th>
                  {/* Coluna Atribuído Para (apenas líderes) */}
                  {user && (user.role === "lider" || user.role === "admin") && (
                    <th className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground border-l hidden lg:table-cell">
                      Atribuído Para
                    </th>
                  )}
                  {/* Coluna Removido Totalmente */}
                  <th className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground border-l hidden xl:table-cell">
                    Rem. Total
                  </th>
                  <th className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground border-l">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {movements.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="text-center py-8 text-muted-foreground">
                      Nenhuma movimentação encontrada
                    </td>
                  </tr>
                ) : (
                  movements.map((movement, index) => (
                    <tr key={movement.id} className={`hover:bg-muted/30 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                      {/* Checkbox para seleção (apenas líderes) */}
                      {user && (user.role === "lider" || user.role === "admin") && (
                        <td className="py-2 px-3 text-center">
                          <Checkbox
                            checked={selectedIds.includes(movement.id)}
                            onCheckedChange={() => handleToggleSelect(movement.id)}
                          />
                        </td>
                      )}
                      <td className="py-2 px-3 text-sm font-mono text-muted-foreground text-center">
                        #{movement.id}
                      </td>
                      <td className="py-2 px-3 text-center border-l">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(
                            movement.status
                          )}`}
                          title={
                            movement.status === "ABERTO" ? "Aguardando operador pegar a tarefa" :
                            movement.status === "EM_ATENDIMENTO" ? "Operador está executando a movimentação" :
                            movement.status === "CONCLUIDO" ? "Movimentação finalizada com fotos" :
                            "Movimentação cancelada"
                          }
                        >
                          {formatStatus(movement.status)}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-center border-l">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getTipoColor(
                            movement.tipo
                          )}`}
                          title={
                            movement.tipo === "ENTRADA" ? "Produto chegando de fornecedor externo" :
                            movement.tipo === "INTERNA" ? "Movimentação entre endereços internos" :
                            movement.tipo === "SAIDA" ? "Produto saindo para expedição/cliente" :
                            "Reabastecimento de picking a partir de estoque"
                          }
                        >
                          {movement.tipo}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-sm font-medium text-center border-l">
                        {(() => {
                          // Contar movimentações abertas do mesmo produto
                          const openCount = movements.filter(
                            (m) => m.produto === movement.produto && m.status === "ABERTO"
                          ).length;
                          const hasMultiple = openCount > 1;

                          return (
                            <div className="flex items-center gap-2">
                              <EditableCell
                                value={movement.produto}
                                onSave={(newValue) => handleUpdateField(movement.id, "produto", newValue)}
                                disabled={movement.status === "CONCLUIDO" || movement.status === "CANCELADO"}
                              />
                              {movement.status === "ABERTO" && openCount > 1 && (
                                <span
                                  className={`inline-flex items-center justify-center px-2 py-0.5 text-xs font-semibold rounded-full ${
                                    hasMultiple
                                      ? "bg-orange-100 text-orange-700 border border-orange-300"
                                      : "bg-muted text-muted-foreground"
                                  }`}
                                  title={`${openCount} movimentações abertas deste produto`}
                                >
                                  {openCount}
                                </span>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="py-2 px-3 text-sm text-muted-foreground text-center border-l">
                        <EditableCell
                          value={movement.referencia || ""}
                          onSave={(newValue) => handleUpdateField(movement.id, "referencia", newValue)}
                          disabled={movement.status === "CONCLUIDO" || movement.status === "CANCELADO"}
                        />
                      </td>
                      <td className="py-2 px-3 text-sm text-muted-foreground text-center border-l">
                        <EditableCell
                          value={movement.numeroPedido || ""}
                          onSave={(newValue) => handleUpdateField(movement.id, "numeroPedido", newValue)}
                          disabled={movement.status === "CONCLUIDO" || movement.status === "CANCELADO"}
                        />
                      </td>
                      <td className="py-2 px-3 text-xs text-muted-foreground text-center border-l">
                        {movement.quantidade ? parseInt(movement.quantidade).toLocaleString("pt-BR") : "-"}
                      </td>
                      <td className="py-2 px-3 text-xs text-center border-l">
                        {(() => {
                          const hoursElapsed = (Date.now() - new Date(movement.criadoEm).getTime()) / (1000 * 60 * 60);
                          const isNotFinished = movement.status !== "CONCLUIDO" && movement.status !== "CANCELADO";
                          const isWarning = isNotFinished && hoursElapsed >= 2 && hoursElapsed < 4;
                          const isCritical = isNotFinished && hoursElapsed >= 4;
                          
                          return (
                            <div className={`flex items-center gap-1 ${
                              isCritical ? "text-red-600 font-medium" : isWarning ? "text-yellow-600 font-medium" : "text-muted-foreground"
                            }`}>
                              {isWarning && (
                                <span className="flex h-2 w-2">
                                  <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-yellow-400 opacity-75"></span>
                                  <span className="relative inline-flex rounded-full h-2 w-2 bg-yellow-500"></span>
                                </span>
                              )}
                              {isCritical && (
                                <span className="flex h-2 w-2">
                                  <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-red-400 opacity-75"></span>
                                  <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                                </span>
                              )}
                              <span>
                                {formatDistanceToNow(new Date(movement.criadoEm), {
                                  addSuffix: true,
                                  locale: ptBR,
                                })}
                              </span>
                            </div>
                          );
                        })()}
                      </td>
                      <td className="py-2 px-3 text-sm text-muted-foreground text-center border-l hidden sm:table-cell">
                        <EditableCell
                          value={movement.origem}
                          onSave={(newValue) => handleUpdateField(movement.id, "origem", newValue)}
                          disabled={movement.status === "CONCLUIDO" || movement.status === "CANCELADO"}
                        />
                      </td>
                      <td className="py-2 px-3 text-sm text-muted-foreground text-center border-l hidden sm:table-cell">
                        <EditableCell
                          value={movement.destino}
                          onSave={(newValue) => handleUpdateField(movement.id, "destino", newValue)}
                          disabled={movement.status === "CONCLUIDO" || movement.status === "CANCELADO"}
                        />
                      </td>
                      {/* Célula Atribuído Para (apenas líderes) */}
                      {user && (user.role === "lider" || user.role === "admin") && (
                        <td className="py-2 px-3 text-sm text-center border-l hidden lg:table-cell">
                          {movement.responsavelId ? (
                            <div className="flex flex-col items-center gap-1">
                              <span className="text-xs font-medium text-blue-600">
                                {movement.responsavelNome || `ID ${movement.responsavelId}`}
                              </span>
                              {movement.atribuidoEm && (
                                <span className="text-xs text-muted-foreground">
                                  {formatDistanceToNow(new Date(movement.atribuidoEm), {
                                    addSuffix: true,
                                    locale: ptBR,
                                  })}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground italic">Não atribuído</span>
                          )}
                        </td>
                      )}
                      {/* Célula Removido Totalmente */}
                      <td className="py-2 px-3 text-center border-l hidden xl:table-cell">
                        {(movement.tipo === "INTERNA" || movement.tipo === "REPOSICAO") ? (
                          <span className={`text-lg ${
                            movement.removidoTotalmente 
                              ? "text-green-600" 
                              : "text-orange-600"
                          }`}>
                            {movement.removidoTotalmente ? "✓" : "✗"}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="py-2 px-3 text-center border-l">
                        <div className="flex items-center justify-end gap-1">
                          {movement.status === "ABERTO" && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handlePegar(movement.id)}
                              className="h-7 px-2 text-xs"
                            >
                              Pegar
                            </Button>
                          )}
                          {movement.status === "EM_ATENDIMENTO" && (
                            <Button
                              size="sm"
                              variant="default"
                              onClick={() => handleConcluir(movement.id)}
                              className="h-7 px-2 text-xs"
                            >
                              Concluir
                            </Button>
                          )}
                          {(movement.status === "ABERTO" ||
                            movement.status === "EM_ATENDIMENTO") && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleCancelar(movement.id)}
                              className="h-7 px-2 text-xs text-destructive hover:text-destructive"
                            >
                              Cancelar
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setSelectedMovementForAudit(movement.id);
                              setAuditDialogOpen(true);
                            }}
                            className="h-7 px-2 text-xs"
                            title="Ver Histórico"
                          >
                            <History className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* Controles de paginação */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t bg-muted/30">
              <div className="text-sm text-muted-foreground">
                Mostrando {startIndex + 1} a {Math.min(endIndex, totalItems)} de {totalItems} movimentações
              </div>
              
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Anterior
                </Button>
                
                <div className="text-sm font-medium">
                  Página {currentPage} de {totalPages}
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Próxima
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Barra flutuante de atribuição (apenas líderes) */}
      {showAssignBar && user && (user.role === "lider" || user.role === "admin") && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-card border shadow-lg rounded-lg p-4 flex items-center gap-4 min-w-[320px]">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            <span className="text-sm font-medium">
              {selectedIds.length} selecionada{selectedIds.length > 1 ? 's' : ''}
            </span>
          </div>
          
          <Select onValueChange={(value) => handleAssign(Number(value))}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Atribuir para..." />
            </SelectTrigger>
            <SelectContent>
              {operators.map((op) => (
                <SelectItem key={op.id} value={String(op.id)}>
                  {op.name || op.email || `Usuário ${op.id}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedIds([])}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
      
      {/* Dialog de confirmação com 2 fotos */}
      <Dialog open={showPhotoDialog} onOpenChange={setShowPhotoDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirmar Conclusão</DialogTitle>
            <DialogDescription>
              Capture 2 fotos para confirmar a conclusão: uma do produto e outra da etiqueta do endereço.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Foto do Produto */}
            <div className="space-y-2">
              <label className="text-sm font-medium">1. Foto do Produto *</label>
              {fotoProduto ? (
                <div className="relative">
                  <img 
                    src={fotoProduto} 
                    alt="Produto" 
                    className="w-full h-48 object-cover rounded-lg border"
                  />
                  <Button
                    variant="destructive"
                    size="sm"
                    className="absolute top-2 right-2"
                    onClick={() => setFotoProduto(null)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  className="w-full h-32 border-dashed"
                  onClick={() => handleCapturePhoto('produto')}
                >
                  <div className="flex flex-col items-center gap-2">
                    <Camera className="h-8 w-8 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Capturar Foto do Produto</span>
                  </div>
                </Button>
              )}
            </div>
            
            {/* Foto da Etiqueta do Endereço */}
            <div className="space-y-2">
              <label className="text-sm font-medium">2. Foto da Etiqueta do Endereço *</label>
              {fotoEndereco ? (
                <div className="relative">
                  <img 
                    src={fotoEndereco} 
                    alt="Etiqueta do Endereço" 
                    className="w-full h-48 object-cover rounded-lg border"
                  />
                  <Button
                    variant="destructive"
                    size="sm"
                    className="absolute top-2 right-2"
                    onClick={() => setFotoEndereco(null)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  className="w-full h-32 border-dashed"
                  onClick={() => handleCapturePhoto('endereco')}
                >
                  <div className="flex flex-col items-center gap-2">
                    <Camera className="h-8 w-8 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Capturar Foto da Etiqueta</span>
                  </div>
                </Button>
              )}
            </div>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowPhotoDialog(false);
                setSelectedMovementId(null);
                setFotoProduto(null);
                setFotoEndereco(null);
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmWithPhotos}
              disabled={!fotoProduto || !fotoEndereco}
            >
              Concluir Movimentação
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <AuditLogDialog
        open={auditDialogOpen}
        onOpenChange={setAuditDialogOpen}
        title="Histórico de Alterações da Movimentação"
        logs={auditLogs}
        isLoading={auditLoading}
      />
    </div>
  );
}

function NewMovementForm({ onSuccess }: { onSuccess: () => void }) {
  const [tipo, setTipo] = useState<MovementType>("INTERNA");
  const [produto, setProduto] = useState("");
  const [origem, setOrigem] = useState("");
  const [destino, setDestino] = useState("");
  const [atribuidoPara, setAtribuidoPara] = useState<number | null>(null);
  const [referencia, setReferencia] = useState("");
  const [observacoes, setObservacoes] = useState("");
  const [numeroPedido, setNumeroPedido] = useState("");
  const [removidoTotalmente, setRemovidoTotalmente] = useState(true);
  const [quantidade, setQuantidade] = useState<string>("");
  const [produtoValid, setProdutoValid] = useState<boolean | null>(null);
  const [origemValid, setOrigemValid] = useState<boolean | null>(null);
  const [destinoValid, setDestinoValid] = useState<boolean | null>(null);
  const [origemProductValid, setOrigemProductValid] = useState<boolean | null>(null);
  const [showLocationsModal, setShowLocationsModal] = useState(false);
  const [showLocationsDestinoModal, setShowLocationsDestinoModal] = useState(false);
  const [showEmptyLocationsModal, setShowEmptyLocationsModal] = useState(false);

  
  // Query para validar produto em tempo real
  const { data: produtoData, isLoading: produtoLoading } = trpc.products.getByCod.useQuery(
    { cod: produto },
    { enabled: produto.trim().length > 0 }
  );
  
  // Query para validar origem (apenas quando necessário)
  const [shouldValidateOrigem, setShouldValidateOrigem] = useState(false);
  const { data: origemData, isLoading: origemLoading } = trpc.addresses.getByEndereco.useQuery(
    { endereco: origem },
    { enabled: shouldValidateOrigem && origem.trim().length > 0 && origem !== "EXTERNO" }
  );
  
  // Query para validar destino (apenas quando necessário)
  const [shouldValidateDestino, setShouldValidateDestino] = useState(false);
  const { data: destinoData, isLoading: destinoLoading } = trpc.addresses.getByEndereco.useQuery(
    { endereco: destino },
    { enabled: shouldValidateDestino && destino.trim().length > 0 && destino !== "EXPEDICAO" }
  );
  
  // Query para buscar localizações do produto (para validar origem)
  const { data: productLocations, isLoading: locationsLoading } = trpc.products.locations.useQuery(
    { cod: produto },
    { enabled: produto.trim().length > 0 && produtoValid === true }
  );
  
  // Query para buscar endereços vazios
  const { data: emptyLocations, isLoading: emptyLocationsLoading } = trpc.addresses.emptyLocations.useQuery();
  
  // Atualizar validação de produto
  useEffect(() => {
    if (!produto.trim()) {
      setProdutoValid(null);
      return;
    }
    if (produtoLoading) return;
    setProdutoValid(!!produtoData);
  }, [produto, produtoData, produtoLoading]);
  
  // Atualizar validação de origem
  useEffect(() => {
    if (!origem.trim()) {
      setOrigemValid(null);
      return;
    }
    // EXTERNO é sempre válido
    if (origem === "EXTERNO") {
      setOrigemValid(true);
      return;
    }
    if (origemLoading) return;
    setOrigemValid(!!origemData);
  }, [origem, origemData, origemLoading]);
  
  // Atualizar validação de destino
  useEffect(() => {
    if (!destino.trim()) {
      setDestinoValid(null);
      return;
    }
    // EXPEDICAO é sempre válido
    if (destino === "EXPEDICAO") {
      setDestinoValid(true);
      return;
    }
    if (destinoLoading) return;
    setDestinoValid(!!destinoData);
  }, [destino, destinoData, destinoLoading]);
  
  // Validar se produto consta na origem
  useEffect(() => {
    if (!produto.trim() || !origem.trim() || origem === "EXTERNO") {
      setOrigemProductValid(null);
      return;
    }
    if (locationsLoading) return;
    setOrigemProductValid(productLocations?.includes(origem) || false);
  }, [produto, origem, productLocations, locationsLoading]);


  const createMutation = trpc.movements.create.useMutation({
    onSuccess: () => {
      toast.success("Movimentação criada com sucesso");
      onSuccess();
      // Limpar campos
      setProduto("");
      setOrigem("");
      setDestino("");
      setReferencia("");
      setObservacoes("");
      setNumeroPedido("");
      setQuantidade("");
      // Focar no campo produto
      setTimeout(() => {
        document.getElementById("produto-input")?.focus();
      }, 100);
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validações
    if (!produto.trim()) {
      toast.error("Produto é obrigatório");
      return;
    }
    if (produtoValid === false) {
      toast.error("Digite um código de produto válido");
      return;
    }
    
    // Validar origem (se não for ENTRADA que usa EXTERNO)
    if (tipo !== "ENTRADA") {
      const errors: string[] = [];
      
      if (origemValid === false) {
        errors.push("Endereço de origem não cadastrado");
      }
      
      if (origemProductValid === false) {
        errors.push(`Produto ${produto} não consta no endereço ${origem}`);
      }
      
      if (errors.length > 0) {
        errors.forEach(error => toast.error(error));
        return;
      }
    }
    
    // Validar destino (se não for SAIDA que usa EXPEDICAO)
    if (tipo !== "SAIDA" && destinoValid === false) {
      toast.error("Digite um endereço de destino válido");
      return;
    }

    // Definir origem/destino conforme tipo
    let finalOrigem = origem;
    let finalDestino = destino;

    if (tipo === "ENTRADA") {
      finalOrigem = "EXTERNO";
      if (!destino.trim()) {
        toast.error("Destino é obrigatório para ENTRADA");
        return;
      }
    } else if (tipo === "SAIDA") {
      finalDestino = "EXPEDICAO";
      if (!origem.trim()) {
        toast.error("Origem é obrigatória para SAÍDA");
        return;
      }
    } else if (tipo === "REPOSICAO") {
      if (!origem.trim() || !destino.trim()) {
        toast.error("Origem e destino são obrigatórios para REPOSIÇÃO");
        return;
      }
      if (!numeroPedido.trim()) {
        toast.error("Número do pedido é obrigatório para REPOSIÇÃO");
        return;
      }
    } else {
      // INTERNA
      if (!origem.trim() || !destino.trim()) {
        toast.error("Origem e destino são obrigatórios para movimentação INTERNA");
        return;
      }
    }

    createMutation.mutate({
      tipo,
      produto: produto.trim(),
      origem: finalOrigem.trim(),
      destino: finalDestino.trim(),
      atribuidoPara: null,
      referencia: referencia.trim() || undefined,
      observacoes: observacoes.trim() || undefined,
      numeroPedido: numeroPedido.trim() || undefined,
      removidoTotalmente: (tipo === "INTERNA" || tipo === "REPOSICAO") ? removidoTotalmente : undefined,
      quantidade: quantidade.trim() ? parseFloat(quantidade) : undefined,
    });
  };

  return (
    <>
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-6 xl:grid-cols-7 gap-2">
        {/* Ordem da tabela: Tipo, Produto, Referência, Nº Pedido, Origem, Destino */}
        <div>
          <label className="text-xs font-medium text-muted-foreground mb-1 block">
            Tipo *
          </label>
          <Select value={tipo} onValueChange={(v) => setTipo(v as MovementType)}>
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ENTRADA">Entrada</SelectItem>
              <SelectItem value="INTERNA">Interna</SelectItem>
              <SelectItem value="SAIDA">Saída</SelectItem>
              <SelectItem value="REPOSICAO">Reposição</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="lg:col-span-2">
          <label className="text-xs font-medium text-muted-foreground mb-1 block">
            Produto *
          </label>
          <div className="relative">
            <Input
              id="produto-input"
              value={produto}
              onChange={(e) => setProduto(e.target.value)}
              placeholder="Nome do produto"
              className={`h-9 pr-8 ${
                produtoValid === true
                  ? "border-green-500 focus-visible:ring-green-500"
                  : produtoValid === false
                  ? "border-red-500 focus-visible:ring-red-500"
                  : ""
              }`}
              autoFocus
            />
            {produtoValid === true && (
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-green-500">✓</span>
            )}
            {produtoValid === false && (
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500">✗</span>
            )}
          </div>
          {produtoValid === false && (
            <p className="text-xs text-red-500 mt-1">Código de produto inválido</p>
          )}
          <div className="flex gap-2 mt-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowLocationsModal(true)}
              disabled={produtoValid !== true}
            >
              <MapPin className="h-3 w-3 mr-1" />
              Ver Localizações de Origem {produtoValid === true && productLocations ? `(${productLocations.length})` : ""}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowLocationsDestinoModal(true)}
              disabled={produtoValid !== true}
            >
              <MapPin className="h-3 w-3 mr-1" />
              Ver Localizações de Destino {produtoValid === true && productLocations ? `(${productLocations.length})` : ""}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowEmptyLocationsModal(true)}
            >
              <MapPin className="h-3 w-3 mr-1" />
              Localizações Vazias {emptyLocations ? `(${emptyLocations.length})` : ""}
            </Button>
          </div>
        </div>

        <div>
          <label className="text-xs font-medium text-muted-foreground mb-1 block">
            Referência
          </label>
          <Input
            value={referencia}
            onChange={(e) => setReferencia(e.target.value)}
            placeholder="REF-001"
            className="h-9"
          />
        </div>

        <div>
          <label className="text-xs font-medium text-muted-foreground mb-1 block">
            Quantidade
          </label>
          <Input
            type="number"
            step="0.0001"
            min="0"
            value={quantidade}
            onChange={(e) => setQuantidade(e.target.value)}
            placeholder="0"
            className="h-9"
          />
        </div>

        {tipo === "REPOSICAO" && (
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">
              Nº Pedido *
            </label>
            <Input
              value={numeroPedido}
              onChange={(e) => setNumeroPedido(e.target.value)}
              placeholder="PED-12345"
              className="h-9"
            />
          </div>
        )}

        {tipo !== "ENTRADA" && (
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">
              Origem *
            </label>
            <div className="relative">
              <Input
                value={origem}
                onChange={(e) => {
                  setOrigem(e.target.value);
                  setShouldValidateOrigem(false);
                }}
                onBlur={() => setShouldValidateOrigem(true)}
                placeholder="01.05.02.03"
                className={`h-9 pr-8 ${
                  origemValid === true && origemProductValid !== false
                    ? "border-green-500"
                    : origemValid === false || origemProductValid === false
                    ? "border-red-500"
                    : ""
                }`}
              />
              {origemValid === true && origemProductValid !== false && (
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-green-500">✓</span>
              )}
              {(origemValid === false || origemProductValid === false) && (
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500">✗</span>
              )}
              {origemProductValid === false && (
                <span className="absolute -bottom-5 left-0 text-xs text-red-500">Produto não consta neste endereço</span>
              )}
            </div>
          </div>
        )}

        {tipo !== "SAIDA" && (
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">
              Destino *
            </label>
            <div className="relative">
              <Input
                value={destino}
                onChange={(e) => {
                  setDestino(e.target.value);
                  setShouldValidateDestino(false);
                }}
                onBlur={() => setShouldValidateDestino(true)}
                placeholder="02.03.01"
                className={`h-9 pr-8 ${
                  destinoValid === true
                    ? "border-green-500"
                    : destinoValid === false
                    ? "border-red-500"
                    : ""
                }`}
              />
              {destinoValid === true && (
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-green-500">✓</span>
              )}
              {destinoValid === false && (
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500">✗</span>
              )}
            </div>
          </div>
        )}
      </div>

      {(tipo === "INTERNA" || tipo === "REPOSICAO") && (
        <div>
          <label className="text-xs font-medium text-muted-foreground mb-1 block">
            Removido Totalmente?
          </label>
          <Select
            value={removidoTotalmente ? "sim" : "nao"}
            onValueChange={(v) => setRemovidoTotalmente(v === "sim")}
          >
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="sim">Sim (item sai completamente da origem)</SelectItem>
              <SelectItem value="nao">Não (item continua na origem)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      <div>
        <label className="text-xs font-medium text-muted-foreground mb-1 block">
          Observações
        </label>
        <Input
          value={observacoes}
          onChange={(e) => setObservacoes(e.target.value)}
          placeholder="Observações adicionais"
          className="h-9"
        />
      </div>

      <div className="flex gap-2">
        <Button type="submit" disabled={createMutation.isPending} className="flex-1">
          {createMutation.isPending ? "Salvando..." : "Salvar e Criar Outro"}
        </Button>
      </div>
    </form>

    {/* Modal de Localizações */}
    <Dialog open={showLocationsModal} onOpenChange={setShowLocationsModal}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Localizações de {produto}</DialogTitle>
          <DialogDescription>
            Endereços onde o produto está localizado atualmente
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[400px] overflow-y-auto">
          {productLocations && productLocations.length > 0 ? (
            productLocations.map((location, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-muted rounded-lg hover:bg-muted/80 transition-colors cursor-pointer"
                onClick={() => {
                  setOrigem(location);
                  setShowLocationsModal(false);
                  toast.success(`Origem preenchida com ${location}`);
                }}
              >
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-blue-600" />
                  <span className="font-mono font-medium">{location}</span>
                </div>
                <Button size="sm" variant="ghost" className="h-7 text-xs">
                  Usar como Origem
                </Button>
              </div>
            ))
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">
              Sem endereços para esse produto
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>

    {/* Modal de Localizações de Destino */}
    <Dialog open={showLocationsDestinoModal} onOpenChange={setShowLocationsDestinoModal}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Localizações de {produto}</DialogTitle>
          <DialogDescription>
            Selecione um endereço para usar como destino
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[400px] overflow-y-auto">
          {productLocations && productLocations.length > 0 ? (
            productLocations.map((location, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-muted rounded-lg hover:bg-muted/80 transition-colors cursor-pointer"
                onClick={() => {
                  setDestino(location);
                  setShowLocationsDestinoModal(false);
                  toast.success(`Destino preenchido com ${location}`);
                }}
              >
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-green-600" />
                  <span className="font-mono font-medium">{location}</span>
                </div>
                <Button size="sm" variant="ghost" className="h-7 text-xs">
                  Usar como Destino
                </Button>
              </div>
            ))
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">
              Sem endereços para esse produto
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>

    {/* Modal de Localizações Vazias */}
    <Dialog open={showEmptyLocationsModal} onOpenChange={setShowEmptyLocationsModal}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Endereços Vazios</DialogTitle>
          <DialogDescription>
            Selecione um endereço vazio para usar como destino
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[400px] overflow-y-auto">
          {emptyLocations && emptyLocations.length > 0 ? (
            emptyLocations.map((location, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-muted rounded-lg hover:bg-muted/80 transition-colors cursor-pointer"
                onClick={() => {
                  setDestino(location);
                  setShowEmptyLocationsModal(false);
                  toast.success(`Destino preenchido com ${location}`);
                }}
              >
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-orange-600" />
                  <span className="font-mono font-medium">{location}</span>
                </div>
                <Button size="sm" variant="ghost" className="h-7 text-xs">
                  Usar como Destino
                </Button>
              </div>
            ))
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">
              Nenhum endereço vazio encontrado
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  </>
  );
}
